/**
 * navigation/appShell.tsx — the signed-in shell: docked sidebar (overlay drawer
 * on mobile) + content area hosting the app navigator. The sidebar navigates
 * via the container ref since it renders outside the navigator.
 */
import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform, Pressable } from 'react-native';
import { LightColors as C } from '../theme/theme';
import { useResponsive } from '../lib/responsive';
import Sidebar from '../shell/sidebar';
import Topbar from '../shell/topbar';
import AppStack from './appStack';

export default function AppShell({ onSignOut }: { onSignOut: () => void }) {
  const { isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isMobile && drawerOpen) setDrawerOpen(false);
  }, [isMobile, drawerOpen]);

  return (
    <View style={styles.app}>
      {!isMobile && <Sidebar onLogout={onSignOut} />}

      <View style={styles.main}>
        {isMobile && <Topbar onMenu={() => setDrawerOpen(true)} />}
        <View style={styles.content}>
          <AppStack />
        </View>
      </View>

      {isMobile && drawerOpen && (
        <View style={styles.overlay}>
          <Sidebar
            mobile
            onLogout={() => {
              setDrawerOpen(false);
              onSignOut();
            }}
            onNavigate={() => setDrawerOpen(false)}
          />
          <Pressable style={styles.scrim} onPress={() => setDrawerOpen(false)} accessibilityLabel="Close menu" />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  app: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: C.paper,
    ...(Platform.OS === 'web' ? { height: '100vh' as unknown as number, overflow: 'hidden' } : {}),
  },
  main: { flex: 1, flexDirection: 'column', minWidth: 0, ...(Platform.OS === 'web' ? { overflow: 'hidden' } : {}) },
  content: { flex: 1, ...(Platform.OS === 'web' ? ({ overflowY: 'auto' } as object) : {}) },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'row',
    ...(Platform.OS === 'web' ? { zIndex: 100 as unknown as number } : {}),
  },
  scrim: { flex: 1, backgroundColor: 'rgba(15,23,42,0.45)' },
});
